import * as Yup from 'yup';
import { capitalize } from 'Helpers/utils';
import { ContextTag } from '../types';

/**
 * Transforms a human-readable tag (e.g., "My New Tag") to backend format (e.g., "my_new_tag")
 * Backend expects: lowercase, alphanumeric, dashes, underscores only
 */
export const transformTagToBackendFormat = (tag: string): string => {
  return tag
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-_]+/g, '_') // Replace invalid chars with underscore
    .replace(/[-_\s]+/g, '_') // Collapse multiple dashes/underscores/spaces to single underscore
    .replace(/^_+|_+$/g, ''); // Remove leading/trailing underscores
};

/**
 * Transforms a backend format tag to human-readable format for display
 * Converts underscores/dashes to spaces and capitalizes words
 */
export const transformTagToHumanReadable = (tag: string): string => {
  return tag
    .toLowerCase()
    .split(/[-_]/)
    .map(word => capitalize(word))
    .join(' ');
};

export const getContextTagLabel = (tag: ContextTag, labels: Record<string, string>): string =>
  labels[tag] || transformTagToHumanReadable(tag);

/**
 * Finds the matching tag from available tags by normalizing and comparing
 * Returns the original tag format from availableTags if found, or null
 */
export const findMatchingTag = (tag: string, availableTags: string[]): string | null => {
  const normalizedTag = transformTagToBackendFormat(tag);
  return (
    availableTags.find(existing => transformTagToBackendFormat(existing) === normalizedTag) || null
  );
};

/**
 * Validation constants
 */
const VALIDATION_RULES = {
  MIN_LENGTH: 3,
  MAX_LENGTH: 30,
  FORMAT_REGEX: /^[a-zA-Z0-9\s-_]+$/,
} as const;

/**
 * Core validation logic
 * Returns error message if invalid, null if valid
 */
const validateTagValue = (
  value: string | undefined,
  normalizedExistingTags: Set<string>,
  duplicateMessage = 'This tag already exists',
): string | null => {
  const trimmed = value?.trim();
  if (!trimmed) {
    return 'Tag name is required';
  }

  if (!VALIDATION_RULES.FORMAT_REGEX.test(trimmed)) {
    return 'Tag name contains invalid characters';
  }

  const normalized = transformTagToBackendFormat(trimmed);
  if (normalized.length < VALIDATION_RULES.MIN_LENGTH) {
    return 'Tag name must be at least 3 characters';
  }
  if (normalized.length > VALIDATION_RULES.MAX_LENGTH) {
    return 'Tag name must be 30 characters or less';
  }

  if (normalizedExistingTags.has(normalized)) {
    return duplicateMessage;
  }

  return null;
};

/**
 * Creates a Yup validation schema for context tag input
 * Uses shared validation logic matching backend rules
 */
export const createContextTagValidationSchema = (existingTags: string[], currentTag?: string) => {
  const normalizedExistingTags = existingTags.map(transformTagToBackendFormat);
  const normalizedCurrentTag = currentTag ? transformTagToBackendFormat(currentTag) : null;
  const tagsToCheck = new Set(normalizedExistingTags.filter(tag => tag !== normalizedCurrentTag));

  return Yup.object().shape({
    tag: Yup.string()
      .required('Tag name is required')
      .matches(VALIDATION_RULES.FORMAT_REGEX, 'Tag name contains invalid characters')
      .test('length-and-unique', value => {
        return validateTagValue(value, tagsToCheck) === null;
      }),
  });
};

/**
 * Validates a context tag for inline creation (e.g., in MultiCombobox)
 * Returns an error message string if invalid, null if valid
 * Uses the same validation rules as createContextTagValidationSchema for consistency
 */
export const validateContextTag = (newTag: string, selectedTags: ContextTag[]): string | null => {
  const normalizedSelected = new Set(selectedTags.map(t => transformTagToBackendFormat(String(t))));
  return validateTagValue(newTag, normalizedSelected, 'This tag is already selected');
};
